import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/menu', label: 'Menu' },
    { path: '/about', label: 'About Us' }
  ];

  const rightNavLinks = [
    { path: '/gallery', label: 'Gallery' },
    { path: '/reviews', label: 'Reviews' },
    { path: '/contact', label: 'Contact Us' }
  ];

  // Fun logo animation sequence
  const logoAnimation = {
    initial: { 
      scale: 0, 
      rotate: -180,
      y: -100,
      opacity: 0
    },
    animate: { 
      scale: [0, 1.2, 0.9, 1.1, 1],
      rotate: [-180, 0, 10, -5, 0],
      y: [-100, 0, -10, 5, 0],
      opacity: [0, 1, 1, 1, 1]
    },
    transition: {
      duration: 2,
      times: [0, 0.3, 0.6, 0.8, 1],
      ease: "easeInOut"
    }
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/90 backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Left Navigation */}
          <nav className="hidden lg:flex space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-white hover:text-yellow-400 transition-colors duration-300 font-medium ${
                  location.pathname === link.path ? 'text-yellow-400' : ''
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Logo with Enhanced Animation */}
          <motion.div
            initial={logoAnimation.initial}
            animate={logoAnimation.animate}
            transition={logoAnimation.transition}
            whileHover={{ 
              scale: 1.1,
              rotate: [0, -5, 5, 0],
              transition: { duration: 0.5 }
            }}
            className="flex-shrink-0 cursor-pointer"
          >
            <Link to="/">
              <motion.img
                src="https://i.ibb.co/rGQMXmBd/Logo.png"
                alt="Zalabya Logo"
                className="h-16 w-auto drop-shadow-lg"
                whileHover={{ 
                  filter: "brightness(1.2)",
                  transition: { duration: 0.3 }
                }}
              />
            </Link>
          </motion.div>

          {/* Right Navigation */}
          <nav className="hidden lg:flex space-x-8">
            {rightNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-white hover:text-yellow-400 transition-colors duration-300 font-medium ${
                  location.pathname === link.path ? 'text-yellow-400' : ''
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-white text-2xl"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden mt-4 bg-black/90 rounded-lg p-4"
          >
            <div className="flex flex-col space-y-4">
              {[...navLinks, ...rightNavLinks].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-white hover:text-yellow-400 transition-colors duration-300 font-medium ${
                    location.pathname === link.path ? 'text-yellow-400' : ''
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Fun Loading Animation Overlay */}
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="fixed inset-0 bg-black/20 pointer-events-none z-40"
      />
    </motion.header>
  );
};

export default Header;